/**
 * What a comparison *is*, before anything is rendered: which vehicles it spans, how that list travels in a URL,
 * and when two run selections are the same aggregation.
 *
 * Deliberately free of i18n, formatting and React — `compare-display.ts` holds everything that needs a
 * translator. Keeping the rules apart from their presentation is what lets the compare screen and the report
 * builder share one definition of "two to four vehicles", and what makes the builder's state helpers testable.
 */

export const MIN_COMPARE_VEHICLES = 2;
export const MAX_COMPARE_VEHICLES = 4;

/** `?vehicleIds=a,b` — the deep link the vehicle detail page's "Compare with…" button produces. */
export const parseVehicleIds = (value: string | null): string[] => {
  if (value === null) {
    return [];
  }

  const ids = value
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '');

  return [...new Set(ids)].slice(0, MAX_COMPARE_VEHICLES);
};

export const serializeVehicleIds = (vehicleIds: readonly string[]): string => vehicleIds.join(',');

/**
 * Checking or unchecking one vehicle of a comparison, capped at four — the shared rule behind the vehicle
 * table on the compare screen and the same table in the report builder, so both refuse a fifth column
 * identically and neither can drift.
 */
export const toggleVehicleId = (current: readonly string[], vehicleId: string): string[] => {
  if (current.includes(vehicleId)) {
    return current.filter((id) => id !== vehicleId);
  }

  return current.length >= MAX_COMPARE_VEHICLES ? [...current] : [...current, vehicleId];
};

/** Two selections are the same when every vehicle kept the same runs, whatever order they arrived in. */
export const sameRunSelection = (a: Record<string, string[]> | null, b: Record<string, string[]> | null): boolean => {
  if (a === null || b === null) {
    return a === b;
  }

  const keys = Object.keys(a);

  if (keys.length !== Object.keys(b).length) {
    return false;
  }

  return keys.every((key) => {
    const left = [...(a[key] ?? [])].sort();
    const right = [...(b[key] ?? [])].sort();

    return left.length === right.length && left.every((id, index) => id === right[index]);
  });
};
